import { NavLink } from "react-router-dom";

import { SummaryCard } from "../components/SummaryCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";
import { DetailCardGrid, DetailMetricGrid, formatCurrency } from "./DeepDiveParts";

export function ParcelPage() {
  const { data, loading, error } = useContent("parcel-page", api.getParcel);

  return (
    <PageContainer
      eyebrow="Supporting Detail - Site & Zoning"
      title="Parcel detail for the Site & Zoning surface"
      lede="This route is the parcel-specific supporting layer beneath Site & Zoning. It carries lot identity, geometry, and remaining application items without trying to function as a competing top-level investor surface."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading parcel summary...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? (
        <div className="stack-xl">
          <DetailMetricGrid
            items={[
              { title: "Zoning", value: data.keyFacts.zoning, detail: data.keyFacts.zoningDescription },
              {
                title: "Area",
                value: `${data.keyFacts.parcelArea.squareFeet.toLocaleString()} sf`,
                detail: `${data.keyFacts.parcelArea.acres} acres`
              },
              { title: "Frontage", value: `${data.keyFacts.frontageFeet} ft`, detail: `Depth ${data.keyFacts.depthFeet} ft` },
              { title: "2025 Assessed Value", value: formatCurrency(data.keyFacts.taxAssessedValue), detail: data.keyFacts.propertyClass }
            ]}
          />

          <DetailCardGrid
            items={[
              {
                eyebrow: "Overview",
                title: data.keyFacts.propertyAddress,
                description: data.parcelOverview,
                fields: [
                  { label: "Parcel ID", value: data.keyFacts.parcelId },
                  { label: "Legal Description", value: data.keyFacts.legalDescription },
                  { label: "County", value: data.keyFacts.county },
                  { label: "Coordinates", value: data.keyFacts.coordinates }
                ]
              },
              {
                eyebrow: "Lot Geometry",
                title: "Small-lot infill constraints stay visible",
                description: "The parcel reads as workable for compact concept testing, but layout efficiency still matters materially.",
                fields: [
                  { label: "Square Feet", value: data.keyFacts.parcelArea.squareFeet.toLocaleString() },
                  { label: "Acres", value: `${data.keyFacts.parcelArea.acres}` },
                  { label: "Frontage", value: `${data.keyFacts.frontageFeet} ft` },
                  { label: "Depth", value: `${data.keyFacts.depthFeet} ft` }
                ]
              }
            ]}
          />

          <div className="grid-two">
            <SummaryCard eyebrow="Highlights" title="What is now established" description="These parcel facts are strong enough to support zoning, concept, and feasibility follow-through.">
              <ul className="bullet-list">
                {data.highlights.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Open Items" title={`${data.openItems.length} carried items`} description="Parcel identity is resolved; these are the remaining applied-development questions.">
              <ul className="bullet-list">
                {data.openItems.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <SummaryCard eyebrow="Conclusion" title={data.status} description={data.conclusion} />

          <SummaryCard
            eyebrow="Parent Surface"
            title="Return to Site & Zoning for the combined investor read"
            description="Use this page when the parcel specifics matter. Use the parent Site & Zoning surface for the full land, access, utilities, and regulatory posture."
          >
            <div className="row-wrap gap-sm overview-go-deeper-actions">
              <NavLink to="/site-zoning" className="link-button link-button-secondary">
                Back to Site & Zoning
              </NavLink>
            </div>
          </SummaryCard>
        </div>
      ) : null}
    </PageContainer>
  );
}
